// =============================================================================
// ESPOLIDAR WEB ADMIN - RESELLERS PAGE
// =============================================================================

import { Link } from 'react-router-dom'
import { useResellers, useDeleteReseller, useCreateReseller } from '../hooks/useResellers'
import { Building2, Users, Cpu, Trash2, Plus, Edit, CheckCircle, XCircle } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { useState } from 'react'
import type { ResellerCreate } from '../types'

export default function ResellersPage() {
  const { data: resellers, isLoading } = useResellers()
  const deleteReseller = useDeleteReseller()
  const createReseller = useCreateReseller()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newReseller, setNewReseller] = useState<ResellerCreate>({
    name: '',
    code: '',
    email: '',
    phone: '',
  })

  const handleDelete = (id: number, name: string) => {
    if (confirm(`Sei sicuro di voler eliminare il reseller "${name}"?`)) {
      deleteReseller.mutate(id)
    }
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createReseller.mutate(newReseller, {
      onSuccess: () => {
        setShowCreateForm(false)
        setNewReseller({ name: '', code: '', email: '', phone: '' })
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reseller</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Nuovo Reseller
        </button>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Nuovo Reseller</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={newReseller.name}
                  onChange={(e) => setNewReseller({ ...newReseller, name: e.target.value })}
                  className="input w-full"
                  required
                  placeholder="Nome azienda"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Codice *
                </label>
                <input
                  type="text"
                  value={newReseller.code}
                  onChange={(e) => setNewReseller({ ...newReseller, code: e.target.value.toUpperCase() })}
                  className="input w-full font-mono"
                  required
                  placeholder="CODICE_UNIVOCO"
                />
                <p className="text-xs text-gray-500 mt-1">Identificativo univoco (es. RES001)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newReseller.email}
                  onChange={(e) => setNewReseller({ ...newReseller, email: e.target.value })}
                  className="input w-full"
                  placeholder="email@azienda.it"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={newReseller.phone}
                  onChange={(e) => setNewReseller({ ...newReseller, phone: e.target.value })}
                  className="input w-full"
                  placeholder="+39 ..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn btn-secondary"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createReseller.isPending}
                >
                  {createReseller.isPending ? 'Creazione...' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resellers && resellers.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Reseller
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Codice
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Dispositivi
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Utenti
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Stato
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {resellers.map((reseller) => (
                <tr key={reseller.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Building2 size={20} className="text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{reseller.name}</p>
                        {reseller.email && (
                          <p className="text-sm text-gray-500">{reseller.email}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {reseller.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Cpu size={16} className="text-gray-400" />
                      <span className="font-medium">{reseller.device_count}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Users size={16} className="text-gray-400" />
                      <span className="font-medium">{reseller.user_count}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {reseller.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle size={12} />
                        Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <XCircle size={12} />
                        Disattivo
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/resellers/${reseller.id}`}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Modifica"
                      >
                        <Edit size={18} />
                      </Link>
                      <button
                        onClick={() => handleDelete(reseller.id, reseller.name)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={deleteReseller.isPending || reseller.device_count > 0 || reseller.user_count > 0}
                        title={
                          reseller.device_count > 0 || reseller.user_count > 0
                            ? 'Rimuovi prima dispositivi e utenti'
                            : 'Elimina'
                        }
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nessun reseller
          </h3>
          <p className="text-gray-500 mb-6">
            Crea il primo reseller per iniziare a organizzare dispositivi e utenti.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Crea Reseller
          </button>
        </div>
      )}
    </div>
  )
}
